// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Svg from "../../primitives/Svg.res.js";
import * as Hover from "../../primitives/Hover.res.js";
import * as Media from "../../primitives/Media.res.js";
import * as React from "react";
import * as Spaces from "../theme/Spaces.res.js";
import * as Checkbox from "../selection-and-input/Checkbox.res.js";
import * as ReactAria from "../../externals/ReactAria.res.js";
import * as TextStyle from "../typography/TextStyle.res.js";
import * as ReactAria$1 from "react-aria";
import * as Breakpoints from "../theme/Breakpoints.res.js";
import * as ReactStately from "../../externals/ReactStately.res.js";
import * as Stdlib_Array from "@rescript/runtime/lib/es6/Stdlib_Array.js";
import * as ReactDOMStyle from "@rescript/react/src/ReactDOMStyle.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as ReactStately$1 from "react-stately";
import * as AnimatedRender from "../../primitives/AnimatedRender.res.js";
import * as ReactDomElement from "../../primitives/ReactDomElement.res.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as PredictiveLoadingBar from "../feedback-indicators/PredictiveLoadingBar.res.js";
import * as UseResizeObserver from "react-aria/private/utils/useResizeObserver";

function fromStaticToReactStatelyTableColumnSize(value) {
  return ReactStately.Table.Column.Size.make(value.NAME === "pct" ? ({
      TAG: "Static",
      _0: {
        NAME: "pct",
        VAL: value.VAL
      }
    }) : ({
      TAG: "Static",
      _0: {
        NAME: "px",
        VAL: value.VAL
      }
    }));
}

function toReactStatelyTableColumnSize(value) {
  let variant = value.NAME;
  return ReactStately.Table.Column.Size.make(variant === "px" ? ({
      TAG: "Static",
      _0: {
        NAME: "px",
        VAL: value.VAL
      }
    }) : (
      variant === "pct" ? ({
          TAG: "Static",
          _0: {
            NAME: "pct",
            VAL: value.VAL
          }
        }) : ({
          TAG: "Dynamic",
          _0: {
            NAME: "fr",
            VAL: value.VAL
          }
        })
    ));
}

let cellContentStyle = {
  display: "flex",
  alignItems: "inherit",
  alignSelf: "stretch",
  flexDirection: "column"
};

let headerContentStyle = {
  display: "flex",
  flexGrow: "1",
  justifyContent: "inherit"
};

function measure(items) {
  let elements = Stdlib_Array.filterMap(items, param => {
    let match = param[1];
    let match$1 = ReactDomElement.fromRef(match.containerRef);
    let match$2 = ReactDomElement.fromRef(match.contentRef);
    if (match$1 !== undefined && match$2 !== undefined) {
      return [
        param[0],
        Primitive_option.valFromOption(match$1),
        Primitive_option.valFromOption(match$2)
      ];
    }
  });
  elements.forEach(param => {
    let style = param[2].style;
    style.position = "absolute";
    style.width = "max-content";
  });
  let widths = new Map();
  elements.forEach(param => {
    let columnKey = param[0];
    let match = getComputedStyle(param[1]);
    let gutterWidth = Stdlib_Array.reduce([
      match.paddingLeft,
      match.paddingRight,
      match.borderLeftWidth,
      match.borderRightWidth
    ], 0, (acc, value) => acc + parseFloat(value));
    let width = Math.ceil(param[2].getBoundingClientRect().width + gutterWidth);
    widths.set(columnKey, Math.max(width, Stdlib_Option.getOr(widths.get(columnKey), 0)));
  });
  elements.forEach(param => {
    let style = param[2].style;
    style.position = "";
    style.width = "";
  });
  return widths;
}

function useItem(registry, columnKey, itemKey, containerRef, contentRef, active) {
  React.useLayoutEffect(() => {
    if (active) {
      registry.invalidate();
    }
  });
  React.useLayoutEffect(() => {
    if (active) {
      registry.register(columnKey, itemKey, {
        containerRef: containerRef,
        contentRef: contentRef
      });
      return () => registry.register(columnKey, itemKey, undefined);
    }
  }, [active]);
}

function use() {
  let itemsRef = React.useRef(new Map());
  let invalidatedRef = React.useRef(false);
  let match = React.useState(() => new Map());
  let setWidths = match[1];
  let match$1 = React.useState(() => 0);
  let setMeasureRequestsCount = match$1[1];
  let requestMeasure = React.useCallback(() => {
    invalidatedRef.current = true;
    setMeasureRequestsCount(count => count + 1 | 0);
  }, []);
  let registry = React.useMemo(() => ({
    register: (columnKey, itemKey, item) => {
      if (item !== undefined) {
        itemsRef.current.set(columnKey + `/` + itemKey, [
          columnKey,
          item
        ]);
      } else {
        itemsRef.current.delete(columnKey + `/` + itemKey);
      }
      requestMeasure();
    },
    invalidate: () => {
      invalidatedRef.current = true;
    }
  }), []);
  React.useEffect(() => Stdlib_Option.map(Primitive_option.fromNullable(document.fonts), fonts => {
    fonts.ready.then(param => requestMeasure());
    fonts.addEventListener("loadingdone", requestMeasure);
    return () => {
      fonts.removeEventListener("loadingdone", requestMeasure);
    };
  }), []);
  React.useLayoutEffect(() => {
    if (invalidatedRef.current) {
      invalidatedRef.current = false;
      let next = measure(Array.from(itemsRef.current.values()));
      setWidths(prev => {
        if (prev.size === next.size && Array.from(next.entries()).every(param => prev.get(param[0]) === param[1])) {
          return prev;
        } else {
          return next;
        }
      });
    }
  });
  return [
    match[0],
    registry
  ];
}

let selectCellWidth = ReactStately.Table.Column.Size.make({
  TAG: "Static",
  _0: {
    NAME: "px",
    VAL: 17
  }
});

let defaultCellWidth = ReactStately.Table.Column.Size.make({
  TAG: "Static",
  _0: {
    NAME: "px",
    VAL: 90
  }
});

function getBoundingRectFromNodeRef(ref) {
  return Stdlib_Option.map(ReactDomElement.fromRef(ref), prim => prim.getBoundingClientRect());
}

function getScrollableXYFromNodeRef(ref) {
  return Stdlib_Option.map(ReactDomElement.fromRef(ref), domElement => [
    ((domElement.scrollLeft | 0) + domElement.clientWidth | 0) < (domElement.scrollWidth - 17 | 0),
    ((domElement.scrollTop | 0) + domElement.clientHeight | 0) < (domElement.scrollHeight - 17 | 0)
  ]);
}

function getScrollBarWidthFromNodeRef(ref) {
  return Stdlib_Option.map(ReactDomElement.fromRef(ref), domElement => (domElement.offsetWidth | 0) - domElement.clientWidth | 0);
}

function getScrollBarHeightFromNodeRef(ref) {
  return Stdlib_Option.map(ReactDomElement.fromRef(ref), domElement => (domElement.offsetHeight | 0) - domElement.clientHeight | 0);
}

function columnStickyToRight(columnIndex, layoutState) {
  let columnSticky = Stdlib_Option.mapOr(layoutState.find(col => col.index === columnIndex), false, col => col.sticky);
  let inLeadingRun = !layoutState.some(col => {
    if (col.index <= columnIndex) {
      return !col.sticky;
    } else {
      return false;
    }
  });
  let inTrailingRun = !layoutState.some(col => {
    if (col.index >= columnIndex) {
      return !col.sticky;
    } else {
      return false;
    }
  });
  if (columnSticky && inTrailingRun) {
    return !inLeadingRun;
  } else {
    return false;
  }
}

function getStickyLeftWidth(layoutState) {
  return Stdlib_Array.reduce(layoutState.filter(col => {
    if (col.sticky) {
      return !layoutState.some(c => {
        if (c.index <= col.index) {
          return !c.sticky;
        } else {
          return false;
        }
      });
    } else {
      return false;
    }
  }), 0, (acc, col) => acc + Stdlib_Option.mapOr(col.relativeRect, 0, prim => prim.width));
}

function getStickyRightWidth(layoutState) {
  return Stdlib_Array.reduce(layoutState.filter(col => columnStickyToRight(col.index, layoutState)), 0, (acc, col) => acc + Stdlib_Option.mapOr(col.relativeRect, 0, prim => prim.width));
}

function isStickyBoundaryColumn(columnIndex, layoutState, side) {
  let neighborIndex = side === "right" ? columnIndex - 1 | 0 : columnIndex + 1 | 0;
  return Stdlib_Option.mapOr(layoutState.find(col => col.index === neighborIndex), true, col => !col.sticky);
}

function computeColumnStickyPositionState(columnIndex, layoutState, scrolled, scrollable) {
  let stickToRight = columnStickyToRight(columnIndex, layoutState);
  let columnSticky = Stdlib_Option.mapOr(layoutState.find(col => col.index === columnIndex), false, col => col.sticky);
  if (stickToRight) {
    let right = Stdlib_Array.reduce(layoutState.filter(col => {
      if (col.sticky) {
        return col.index > columnIndex;
      } else {
        return false;
      }
    }), 0, (acc, col) => acc + Stdlib_Option.mapOr(col.relativeRect, 0, prim => prim.width));
    let atBoundary = isStickyBoundaryColumn(columnIndex, layoutState, "right");
    return [
      "right",
      right,
      scrollable,
      atBoundary
    ];
  }
  let stickyColumns = columnSticky ? layoutState.filter(col => {
      if (col.sticky) {
        return col.index <= columnIndex;
      } else {
        return false;
      }
    }) : [];
  let match = Stdlib_Array.reduce(stickyColumns, [
    0,
    0,
    false
  ], (acc, curr) => {
    let cumulatedStickyWidth = acc[0];
    let left = Stdlib_Option.mapOr(curr.relativeRect, 0, prim => prim.left);
    let width = Stdlib_Option.mapOr(curr.relativeRect, 0, prim => prim.width);
    let nextCumulatedStickyWidth = cumulatedStickyWidth + width;
    let sticking = left - cumulatedStickyWidth <= 0 && scrolled;
    return [
      nextCumulatedStickyWidth,
      cumulatedStickyWidth,
      sticking
    ];
  });
  let atBoundary$1 = isStickyBoundaryColumn(columnIndex, layoutState, "left");
  return [
    "left",
    match[1],
    match[2],
    atBoundary$1
  ];
}

function getRelativeBoundingRect(src, dest) {
  return new DOMRect(dest.left - src.left, dest.top - src.top, dest.width, dest.height);
}

function layoutState(tableRect, columns) {
  return columns.map(column => {
    if (column.props.isSelectionCell) {
      return {
        key: column.key,
        index: column.index,
        relativeRect: undefined,
        sticky: false
      };
    }
    let match = Stdlib_Option.flatMap(column.props.extraColumnProps.ref, getBoundingRectFromNodeRef);
    return {
      key: column.key,
      index: column.index,
      relativeRect: tableRect !== undefined && match !== undefined ? Primitive_option.some(getRelativeBoundingRect(Primitive_option.valFromOption(tableRect), Primitive_option.valFromOption(match))) : undefined,
      sticky: Stdlib_Option.getOr(column.props.extraColumnProps.sticky, false)
    };
  });
}

function use$1(tableRef, headerRef, bodyRef, tableState, onLoadMore) {
  let match = React.useState(() => {});
  let setTableRect = match[1];
  let tableRect = match[0];
  let match$1 = React.useState(() => [
    0,
    0
  ]);
  let setScrollPositions = match$1[1];
  let scrollPositions = match$1[0];
  let match$2 = React.useState(() => 0);
  let setScrollBarWidth = match$2[1];
  let scrollBarWidth = match$2[0];
  let match$3 = React.useState(() => 0);
  let setScrollBarHeight = match$3[1];
  let scrollBarHeight = match$3[0];
  let match$4 = React.useState(() => 0);
  let setBodyHeight = match$4[1];
  let bodyHeight = match$4[0];
  let match$5 = React.useState(() => [
    false,
    false
  ]);
  let setScrollableXY = match$5[1];
  let scrollableXY = match$5[0];
  let scrollabilitySignature = scrollPositions[0].toString() + `-` + scrollPositions[1].toString() + `-` + tableState.collection.rows.length.toString() + `-` + Stdlib_Option.mapOr(tableRect, 0, prim => prim.width).toString() + `-` + scrollBarWidth.toString() + `-` + scrollBarHeight.toString();
  React.useLayoutEffect(() => {
    let match = getScrollableXYFromNodeRef(bodyRef);
    if (match !== undefined) {
      let y = match[1];
      let x = match[0];
      if (x !== scrollableXY[0] || y !== scrollableXY[1]) {
        setScrollableXY(param => [
          x,
          y
        ]);
      }
    }
  }, [scrollabilitySignature]);
  React.useLayoutEffect(() => {
    let headerDomElement = ReactDomElement.fromRef(headerRef);
    let bodyDomElement = ReactDomElement.fromRef(bodyRef);
    let handler = param => {
      let match = Stdlib_Option.map(bodyDomElement, bodyDomElement => bodyDomElement.scrollLeft);
      let match$1 = Stdlib_Option.map(bodyDomElement, bodyDomElement => bodyDomElement.scrollTop);
      let x = scrollPositions[0];
      if (headerDomElement !== undefined && match !== undefined && match$1 !== undefined && (x !== match || scrollPositions[1] !== match$1 || x === 0)) {
        Primitive_option.valFromOption(headerDomElement).scrollLeft = match;
        return setScrollPositions(param => [
          match,
          match$1
        ]);
      }
    };
    setTableRect(param => getBoundingRectFromNodeRef(tableRef));
    Stdlib_Option.forEach(bodyDomElement, domNode => {
      domNode.addEventListener("scroll", handler);
    });
    return () => Stdlib_Option.forEach(bodyDomElement, domEmement => {
      domEmement.removeEventListener("scroll", handler);
    });
  }, []);
  React.useLayoutEffect(() => {
    let domElement = ReactDomElement.fromRef(bodyRef);
    let handler = param => {
      if (domElement === undefined) {
        return;
      }
      if (onLoadMore === undefined) {
        return;
      }
      let domElement$1 = Primitive_option.valFromOption(domElement);
      let scrolledY = domElement$1.scrollTop + domElement$1.offsetHeight;
      let scrolledY$1 = scrolledY | 0;
      if ((domElement$1.scrollHeight - scrolledY$1 | 0) < 100) {
        return onLoadMore();
      }
    };
    Stdlib_Option.forEach(domElement, domElement => {
      domElement.removeEventListener("scroll", handler);
    });
    Stdlib_Option.forEach(domElement, domElement => {
      domElement.addEventListener("scroll", handler);
    });
    return () => Stdlib_Option.forEach(domElement, domElement => {
      domElement.removeEventListener("scroll", handler);
    });
  }, [onLoadMore]);
  UseResizeObserver.useResizeObserver({
    ref: tableRef,
    onResize: () => {
      let match = getBoundingRectFromNodeRef(tableRef);
      if (tableRect !== undefined && match !== undefined && Primitive_option.valFromOption(tableRect).width !== Primitive_option.valFromOption(match).width) {
        setTimeout(() => setTableRect(param => getBoundingRectFromNodeRef(tableRef)), 0);
        return;
      }
    }
  });
  UseResizeObserver.useResizeObserver({
    ref: bodyRef,
    onResize: () => {
      let width = getScrollBarWidthFromNodeRef(bodyRef);
      if (width !== undefined && width !== scrollBarWidth) {
        setTimeout(() => setScrollBarWidth(param => width), 0);
      }
      let height = getScrollBarHeightFromNodeRef(bodyRef);
      if (height !== undefined && height !== scrollBarHeight) {
        setTimeout(() => setScrollBarHeight(param => height), 0);
      }
      let el = ReactDomElement.fromRef(bodyRef);
      if (el === undefined) {
        return;
      }
      let height$1 = Primitive_option.valFromOption(el).clientHeight;
      if (height$1 !== bodyHeight) {
        setTimeout(() => setBodyHeight(param => height$1), 0);
        return;
      }
    }
  });
  let getDefaultWidth = React.useCallback(node => {
    if (node.props.isSelectionCell) {
      return Primitive_option.some(selectCellWidth);
    }
  }, []);
  let getDefaultMinWidth = React.useCallback(node => {
    if (node.props.isSelectionCell) {
      return Primitive_option.some(selectCellWidth);
    } else {
      return Primitive_option.some(defaultCellWidth);
    }
  }, []);
  let match$6 = ReactDomElement.fromRef(tableRef);
  let match$7 = tableState.selectionManager.selectionMode;
  let tableWidth;
  if (match$6 !== undefined) {
    let el = Primitive_option.valFromOption(match$6);
    tableWidth = match$7 === "multiple" ? el.clientWidth - 17 : el.clientWidth - scrollBarWidth - 2;
  } else {
    tableWidth = undefined;
  }
  let scrollBarX_position = scrollPositions[0];
  let scrollBarX_scrollable = scrollableXY[0];
  let scrollBarX_size = scrollBarHeight;
  let scrollBarX = {
    position: scrollBarX_position,
    scrollable: scrollBarX_scrollable,
    size: scrollBarX_size
  };
  let scrollBarY_position = scrollPositions[1];
  let scrollBarY_scrollable = scrollableXY[1];
  let scrollBarY_size = scrollBarWidth;
  let scrollBarY = {
    position: scrollBarY_position,
    scrollable: scrollBarY_scrollable,
    size: scrollBarY_size
  };
  let columnResizeState = ReactStately.Table.useColumnResizeState({
    tableWidth: tableWidth,
    getDefaultWidth: getDefaultWidth,
    getDefaultMinWidth: getDefaultMinWidth
  }, tableState);
  return {
    state: layoutState(tableRect, tableState.collection.columns),
    ariaState: columnResizeState,
    headerRef: headerRef,
    bodyRef: bodyRef,
    tableWidth: tableWidth,
    bodyHeight: bodyHeight,
    scrollBarX: scrollBarX,
    scrollBarY: scrollBarY
  };
}

let style = {
  display: "flex"
};

function makeScrollBarOffsetStyle(scrollBarWidth) {
  return {
    minWidth: String(scrollBarWidth) + `px`
  };
}

let make = React.memo(props => {
  let ref = React.useRef(null);
  let match = ReactAria$1.useTableHeaderRow({
    node: props.item
  }, props.state, ref);
  let scrollBarWidth = Stdlib_Option.getOr(props.layout.scrollBarY.size, 0);
  let newrecord = {...match.rowProps};
  return JsxRuntime.jsxs("tr", (newrecord.style = style, newrecord.ref = Primitive_option.some(ref), newrecord.children = [
    props.children,
    JsxRuntime.jsx("div", {
      style: makeScrollBarOffsetStyle(scrollBarWidth)
    })
  ], newrecord));
});

function style$1(width) {
  return {
    backgroundColor: "#fefefe",
    display: "flex",
    paddingRight: "5px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    width: String(width) + `px`,
    alignSelf: "flex-end"
  };
}

let make$1 = React.memo(props => {
  let state = props.state;
  let column = props.column;
  let ref = React.useRef(null);
  let match = ReactAria$1.useTableColumnHeader({
    node: column
  }, state, ref);
  let match$1 = ReactAria$1.useTableSelectAllCheckbox(state);
  let checkboxProps = match$1.checkboxProps;
  let width = props.layout.ariaState.getColumnWidth(column.key);
  let newrecord = {...match.columnHeaderProps};
  newrecord.style = style$1(width);
  newrecord.ref = Primitive_option.some(ref);
  let match$2 = state.selectionManager.selectionMode;
  newrecord.children = match$2 === "multiple" && !props.disabled ? JsxRuntime.jsx(Checkbox.make, {
      ariaProps: checkboxProps
    }) : JsxRuntime.jsx(ReactAria$1.VisuallyHidden, {
      children: checkboxProps["aria-label"]
    });
  return JsxRuntime.jsx("th", newrecord);
});

function style$2(sortable, fromFirstColumn, fromLastColumn, minWidth, width, side, offset, sticky, sticking, atBoundary, marginOpt, alignXOpt, param) {
  let margin = marginOpt !== undefined ? marginOpt : "xsmall";
  let alignX = alignXOpt !== undefined ? alignXOpt : "flexStart";
  let tmp = {
    backgroundColor: "#fefefe",
    color: "#a2a1b0",
    cursor: sortable ? "pointer" : "default",
    display: "flex",
    lineHeight: "14px",
    minWidth: String(minWidth) + `px`,
    paddingRight: fromLastColumn ? "20px" : (
        alignX === "flexEnd" ? "0" : Spaces.toPx(margin)
      ),
    paddingBottom: "10px",
    paddingLeft: fromFirstColumn ? "20px" : "10px",
    position: sticky ? "sticky" : "initial",
    textAlign: "start",
    width: String(width) + `px`,
    boxShadow: (
      sticking && atBoundary ? "#fefefe" : "transparent"
    ) + ` ` + (
      side === "right" ? "-4px" : "4px"
    ) + ` 0px 16px 4px`,
    justifyContent: alignX === "flexStart" ? "start" : (
        alignX === "flexEnd" ? "end" : "center"
      ),
    boxSizing: "border-box"
  };
  let tmp$1 = side === "right" ? undefined : String(offset) + `px`;
  if (tmp$1 !== undefined) {
    tmp.left = tmp$1;
  }
  let tmp$2 = side === "right" ? String(offset) + `px` : undefined;
  if (tmp$2 !== undefined) {
    tmp.right = tmp$2;
  }
  let tmp$3 = sticking ? "2" : undefined;
  if (tmp$3 !== undefined) {
    tmp.zIndex = tmp$3;
  }
  return tmp;
}

let make$2 = React.memo(props => {
  let layout = props.layout;
  let state = props.state;
  let column = props.column;
  let match = Hover.use(undefined, undefined, undefined);
  let hovered = match[1];
  let ref = match[0];
  let fitContentRef = React.useRef(null);
  let match$1 = ReactAria$1.useTableColumnHeader({
    node: column
  }, state, ref);
  let match$2 = ReactAria.Focus.useRing(undefined, undefined);
  column.props.extraColumnProps.ref = ref;
  let breakpoint = column.props.extraColumnProps.breakpoint;
  let breakpointMatched = Media.use(Breakpoints.toMediaQuery(breakpoint));
  let minWidth = layout.ariaState.getColumnMinWidth(column.key);
  let width = layout.ariaState.getColumnWidth(column.key);
  let fromFirstColumn = column.index === 0;
  let fromLastColumn = column.index === (state.collection.columnCount - 1 | 0);
  let match$3 = column.props.extraColumnProps;
  let sticky = Stdlib_Option.getOr(column.props.extraColumnProps.sticky, false);
  let fitContent = Stdlib_Option.getOr(column.props.extraColumnProps.fitContent, false);
  useItem(props.fitContentRegistry, column.key, column.key, ref, fitContentRef, fitContent && breakpointMatched);
  let match$4 = React.useMemo(() => computeColumnStickyPositionState(column.index, layout.state, layout.scrollBarX.position > 0, layout.scrollBarX.scrollable), [
    layout.scrollBarX.position,
    layout.scrollBarX.scrollable
  ]);
  let sortDirection = Stdlib_Option.flatMap(Primitive_option.fromNullable(state.sortDescriptor), descriptor => {
    if (descriptor.column === column.key) {
      return descriptor.direction;
    }
  });
  let sortable = column.props.allowsSorting;
  let sortableOrSorted = Stdlib_Option.isSome(sortDirection) || hovered && sortable;
  if (!breakpointMatched) {
    return null;
  }
  let newrecord = {...ReactAria$1.mergeProps(match$1.columnHeaderProps, match$2.focusProps)};
  newrecord.colSpan = column.colspan;
  newrecord.style = style$2(sortable, fromFirstColumn, fromLastColumn, minWidth, width, match$4[0], match$4[1], sticky, match$4[2], match$4[3], match$3.margin, match$3.alignX, undefined);
  newrecord.ref = Primitive_option.some(ref);
  let headerAction = column.props.extraColumnProps.headerActionComponent;
  let content = JsxRuntime.jsxs(JsxRuntime.Fragment, {
    children: [
      JsxRuntime.jsx(TextStyle.make, {
        children: column.rendered,
        variation: sortableOrSorted ? "neutral" : "normal",
        weight: Stdlib_Option.isSome(sortDirection) ? "semibold" : "medium",
        size: "tiny",
        maxLines: 1
      }),
      sortDirection !== undefined ? (
          sortable ? JsxRuntime.jsx(Svg.make, {
              children: JsxRuntime.jsx(Svg.Path.make, {
                d: sortDirection === "ascending" ? "M 4.707 9.707 C 4.317 10.098 3.683 10.098 3.293 9.707 C 2.902 9.317 2.902 8.683 3.293 8.293 L 10 1.586 L 16.707 8.293 C 17.098 8.683 17.098 9.317 16.707 9.707 C 16.317 10.098 15.683 10.098 15.293 9.707 L 11 5.414 L 11 19 C 11 19.552 10.552 20 10 20 C 9.448 20 9 19.552 9 19 L 9 5.414 L 4.707 9.707 Z" : "M4.70711 11.2929C4.31658 10.9024 3.68342 10.9024 3.29289 11.2929C2.90237 11.6834 2.90237 12.3166 3.29289 12.7071L10 19.4142L16.7071 12.7071C17.0976 12.3166 17.0976 11.6834 16.7071 11.2929C16.3166 10.9024 15.6834 10.9024 15.2929 11.2929L11 15.5858V2C11 1.44772 10.5523 1 10 1C9.44772 1 9 1.44772 9 2V15.5858L4.70711 11.2929Z",
                fill: "#25243a",
                transform: "translate(2 1)"
              }),
              width: "14",
              height: "13",
              viewBox: "0 0 20 20"
            }) : null
        ) : (
          sortable ? JsxRuntime.jsx(Svg.make, {
              children: JsxRuntime.jsx(Svg.Path.make, {
                d: "M16.2929 5.70714C16.6834 6.09766 17.3166 6.09766 17.7071 5.70714C18.0976 5.31661 18.0976 4.68345 17.7071 4.29292L14 0.585815L10.2929 4.29292C9.90237 4.68345 9.90237 5.31661 10.2929 5.70714C10.6834 6.09766 11.3166 6.09766 11.7071 5.70714L13 4.41424V14C13 14.5523 13.4477 15 14 15C14.5523 15 15 14.5523 15 14V4.41424L16.2929 5.70714ZM3.70711 14.2929C3.31658 13.9024 2.68342 13.9024 2.29289 14.2929C1.90237 14.6834 1.90237 15.3166 2.29289 15.7071L6 19.4142L9.70711 15.7071C10.0976 15.3166 10.0976 14.6834 9.70711 14.2929C9.31658 13.9024 8.68342 13.9024 8.29289 14.2929L7 15.5858L7 6.00006C7 5.44778 6.55229 5.00006 6 5.00006C5.44771 5.00006 5 5.44778 5 6.00006L5 15.5858L3.70711 14.2929Z",
                fill: hovered ? "#25243a" : "#8c8B9b",
                transform: "translate(2 1)"
              }),
              width: "14",
              height: "13",
              viewBox: "0 0 20 20"
            }) : null
        ),
      headerAction !== undefined ? JsxRuntime.jsx("div", {
          children: headerAction,
          style: {
            marginTop: "1px",
            marginLeft: "3px"
          }
        }) : null
    ]
  });
  newrecord.children = fitContent ? JsxRuntime.jsx("div", {
      children: content,
      ref: Primitive_option.some(fitContentRef),
      style: headerContentStyle
    }) : content;
  return JsxRuntime.jsx("th", newrecord);
});

let theadStyle = {
  borderBottom: "1px solid",
  borderBottomColor: "#e7e7ee",
  display: "block",
  minHeight: "26px",
  overflow: "hidden"
};

function tbodyStyle(minHeight, maxHeight, hasEvenRows) {
  let minHeight$1 = Stdlib_Option.mapOr(minHeight, "auto", height => String(height) + "px");
  let maxHeight$1 = Stdlib_Option.mapOr(maxHeight, "auto", height => String(height) + "px");
  return ReactDOMStyle.unsafeAddProp({
    backgroundColor: hasEvenRows ? "#fefefe" : "#f8f8fb",
    display: "flex",
    height: "100%",
    maxHeight: maxHeight$1,
    minHeight: minHeight$1,
    overflow: "auto",
    flexDirection: "column"
  }, "overscrollBehaviorX", "none");
}

let make$3 = React.memo(props => {
  let layout = props.layout;
  let children = props.children;
  let match = ReactAria$1.useTableRowGroup();
  let rowGroupProps = match.rowGroupProps;
  let headerRef = layout.headerRef;
  let bodyRef = layout.bodyRef;
  let hasEvenRows = (React.Children.count(children) - 1 | 0) % 2 === 0;
  if (props.type_ === "thead") {
    let newrecord = {...rowGroupProps};
    return JsxRuntime.jsx("thead", (newrecord.style = theadStyle, newrecord.ref = Primitive_option.some(headerRef), newrecord.children = children, newrecord));
  }
  let newrecord$1 = {...rowGroupProps};
  return JsxRuntime.jsx("tbody", (newrecord$1.style = tbodyStyle(props.minHeight, props.maxHeight, hasEvenRows), newrecord$1.ref = Primitive_option.some(bodyRef), newrecord$1.children = children, newrecord$1));
});

let theadRowStyle = {
  display: "flex"
};

let tbodyRowErrorMarkerStyle = {
  backgroundColor: "#e61e5a",
  height: "100%",
  position: "absolute",
  width: "2px",
  zIndex: "2"
};

function tbodyRowStyle(even, selectable, selected, hovered, errored, minWidth, compact) {
  let rowColor = errored ? "#ffedef" : (
      selected ? "#fdf2fb" : (
          hovered ? "#f3f3f7" : (
              even ? "#f8f8fb" : "#fefefe"
            )
        )
    );
  return {
    backgroundColor: rowColor,
    cursor: selectable ? "default" : "auto",
    display: "flex",
    maxHeight: compact ? "46px" : "unset",
    minHeight: compact ? "46px" : "unset",
    minWidth: String(minWidth) + `px`,
    position: "relative",
    boxShadow: rowColor + ` -1px 0 0 0`
  };
}

let make$4 = React.memo(props => {
  let errored = props.errored;
  let state = props.state;
  let item = props.item;
  let children = props.children;
  let match = Hover.use(undefined, undefined, undefined);
  let ref = match[0];
  let match$1 = ReactAria$1.useTableRow({
    node: item
  }, state, ref);
  let rowProps = match$1.rowProps;
  let match$2 = ReactAria.Focus.useRing(undefined, undefined);
  let focusProps = match$2.focusProps;
  let selectable = state.selectionManager.selectionMode !== "none";
  let selected = state.selectionManager.isSelected(item.key);
  let disabled = state.selectionManager.isDisabled(item.key);
  let match$3 = item.type;
  if (match$3 === "thead") {
    let newrecord = {...ReactAria$1.mergeProps(rowProps, focusProps)};
    return JsxRuntime.jsx("tr", (newrecord.style = theadRowStyle, newrecord.ref = Primitive_option.some(ref), newrecord.children = children, newrecord));
  }
  let even = item.index % 2 === 0;
  let hovered = match[1] && !disabled;
  let minWidth = Stdlib_Option.getOr(props.layout.tableWidth, 0);
  let newrecord$1 = {...ReactAria$1.mergeProps(rowProps, focusProps)};
  return JsxRuntime.jsxs("tr", (newrecord$1.style = tbodyRowStyle(even, selectable, selected, hovered, errored, minWidth, props.compactRows), newrecord$1.className = match$2.isFocusVisible ? "focus-visible-ring" : undefined, newrecord$1.ref = Primitive_option.some(ref), newrecord$1.children = [
    errored ? JsxRuntime.jsx("div", {
        style: tbodyRowErrorMarkerStyle
      }) : null,
    children
  ], newrecord$1));
});

function style$3(width, fromLastRow) {
  return {
    backgroundColor: "inherit",
    borderBottom: fromLastRow ? "unset" : `1px solid ` + "#e7e7ee",
    display: "flex",
    paddingTop: "1px",
    paddingRight: "5px",
    paddingLeft: "20px",
    width: String(width) + `px`,
    alignItems: "center"
  };
}

let make$5 = React.memo(props => {
  let state = props.state;
  let cell = props.cell;
  let ref = React.useRef(null);
  let match = ReactAria$1.useTableCell({
    node: cell
  }, state, ref);
  let match$1 = ReactAria$1.useTableSelectionCheckbox({
    key: cell.parentKey
  }, state);
  let width = props.layout.ariaState.getColumnWidth(cell.column.key);
  let newrecord = {...match.gridCellProps};
  return JsxRuntime.jsx("td", (newrecord.style = style$3(width, props.fromLastRow), newrecord.ref = Primitive_option.some(ref), newrecord.children = JsxRuntime.jsx(Checkbox.make, {
    ariaProps: match$1.checkboxProps
  }), newrecord));
});

function style$4(fromFirstColumn, fromLastColumn, fromLastRow, minWidth, width, side, offset, stickyOpt, stickingOpt, atBoundaryOpt, marginOpt, alignXOpt, alignYOpt, compact, param) {
  let sticky = stickyOpt !== undefined ? stickyOpt : false;
  let sticking = stickingOpt !== undefined ? stickingOpt : false;
  let atBoundary = atBoundaryOpt !== undefined ? atBoundaryOpt : false;
  let margin = marginOpt !== undefined ? marginOpt : "xsmall";
  let alignX = alignXOpt !== undefined ? alignXOpt : "flexStart";
  let alignY = alignYOpt !== undefined ? alignYOpt : "center";
  let tmp = {
    backgroundColor: "inherit",
    borderRight: `1px solid ` + (
      sticking && atBoundary && side !== "right" ? "#d7d7e0" : "transparent"
    ),
    borderBottom: fromLastRow ? "unset" : `1px solid ` + "#e7e7ee",
    display: "flex",
    minWidth: String(minWidth) + `px`,
    overflow: "hidden",
    paddingTop: compact ? "8px" : "10px",
    paddingRight: fromLastColumn ? "20px" : (
        alignX === "flexEnd" ? "0" : Spaces.toPx(margin)
      ),
    paddingBottom: compact ? "8px" : "10px",
    paddingLeft: fromFirstColumn ? "20px" : "10px",
    position: sticky ? "sticky" : "initial",
    width: String(width) + `px`,
    boxShadow: sticking ? "inherit" : "unset",
    alignItems: alignX === "center" ? "center" : (
        alignX === "flexStart" ? "flex-start" : (
            alignX === "flexEnd" ? "flex-end" : "normal"
          )
      ),
    flexDirection: "column",
    justifyContent: alignY === "center" ? "center" : (
        alignY === "flexStart" ? "flex-start" : (
            alignY === "flexEnd" ? "flex-end" : "normal"
          )
      ),
    boxSizing: "border-box"
  };
  let tmp$1 = side === "right" ? `1px solid ` + (
      sticking && atBoundary ? "#d7d7e0" : "transparent"
    ) : undefined;
  if (tmp$1 !== undefined) {
    tmp.borderLeft = tmp$1;
  }
  let tmp$2 = side === "right" ? undefined : String(offset) + `px`;
  if (tmp$2 !== undefined) {
    tmp.left = tmp$2;
  }
  let tmp$3 = side === "right" ? String(offset) + `px` : undefined;
  if (tmp$3 !== undefined) {
    tmp.right = tmp$3;
  }
  let tmp$4 = sticking ? "2" : undefined;
  if (tmp$4 !== undefined) {
    tmp.zIndex = tmp$4;
  }
  return tmp;
}

let make$6 = React.memo(props => {
  let layout = props.layout;
  let state = props.state;
  let cell = props.cell;
  let containerRef = React.useRef(null);
  let fitContentRef = React.useRef(null);
  let match = ReactAria$1.useTableCell({
    node: cell
  }, state, containerRef);
  let match$1 = ReactAria.Focus.useRing(undefined, undefined);
  let match$2 = cell.column.props.extraColumnProps;
  let breakpoint = match$2.breakpoint;
  let breakpointMatched = Media.use(Breakpoints.toMediaQuery(breakpoint));
  let width = layout.ariaState.getColumnWidth(cell.column.key);
  let minWidth = layout.ariaState.getColumnMinWidth(cell.column.key);
  let initialWidthRef = React.useRef(width > 0 ? width : Stdlib_Option.getOr(cell.column.props.defaultWidth, minWidth));
  let fitContent = Stdlib_Option.getOr(cell.column.props.extraColumnProps.fitContent, false);
  useItem(props.fitContentRegistry, cell.column.key, cell.key, containerRef, fitContentRef, fitContent && breakpointMatched);
  React.useLayoutEffect(() => {
    if (fitContent) {
      
    } else if (breakpointMatched) {
      if (breakpoint !== "xsmall") {
        layout.ariaState.updateResizedColumns(cell.column.key, initialWidthRef.current);
      }
    } else {
      layout.ariaState.updateResizedColumns(cell.column.key, 0);
    }
  }, [breakpointMatched]);
  let fromFirstColumn = cell.column.index === 0;
  let fromLastColumn = cell.column.index === (state.collection.columnCount - 1 | 0);
  let match$3 = cell.column.props.extraColumnProps;
  let sticky = Stdlib_Option.getOr(cell.column.props.extraColumnProps.sticky, false);
  let match$4 = React.useMemo(() => computeColumnStickyPositionState(cell.column.index, layout.state, layout.scrollBarX.position > 0, layout.scrollBarX.scrollable), [
    layout.scrollBarX.position,
    layout.scrollBarX.scrollable
  ]);
  if (!breakpointMatched) {
    return null;
  }
  let newrecord = {...ReactAria$1.mergeProps(match.gridCellProps, match$1.focusProps)};
  return JsxRuntime.jsx("td", (newrecord.style = style$4(fromFirstColumn, fromLastColumn, props.fromLastRow, minWidth, width, match$4[0], match$4[1], sticky, match$4[2], match$4[3], match$3.margin, match$3.alignX, match$3.alignY, props.compactRows, undefined), newrecord.className = match$1.isFocusVisible ? "focus-visible-ring" : undefined, newrecord.ref = Primitive_option.some(containerRef), newrecord.children = fitContent ? JsxRuntime.jsx("div", {
      children: cell.rendered,
      ref: Primitive_option.some(fitContentRef),
      style: cellContentStyle
    }) : cell.rendered, newrecord));
});

function verticalGradientOverflowStyle(side, offsetOpt, bodyHeight, param) {
  let offset = offsetOpt !== undefined ? offsetOpt : 0;
  let maskImage = `linear-gradient(to bottom, transparent, black ` + String(50) + `px, black calc(100% - ` + String(50) + `px), transparent)`;
  let tmp = {
    backgroundImage: `linear-gradient(to ` + (
      side === "left" ? "right" : "left"
    ) + `, rgba(0, 0, 0, 0.06), transparent)`,
    height: String(bodyHeight) + `px`,
    position: "absolute",
    top: "25px",
    width: "10px",
    zIndex: "2",
    maskImage: maskImage,
    pointerEvents: "none"
  };
  let tmp$1 = side === "left" ? String(offset) + `px` : undefined;
  if (tmp$1 !== undefined) {
    tmp.left = tmp$1;
  }
  let tmp$2 = side === "right" ? String(offset) + `px` : undefined;
  if (tmp$2 !== undefined) {
    tmp.right = tmp$2;
  }
  return ReactDOMStyle.unsafeAddProp(tmp, "WebkitMaskImage", maskImage);
}

function horizontalGradientOverflowStyle(side, offsetOpt, param) {
  let offset = offsetOpt !== undefined ? offsetOpt : 0;
  let tmp = {
    backgroundImage: `linear-gradient(to ` + (
      side === "top" ? "bottom" : "top"
    ) + `, rgba(0, 0, 0, 0.06), transparent)`,
    height: `13px`,
    left: "0",
    position: "absolute",
    right: "0",
    zIndex: "2",
    pointerEvents: "none"
  };
  let tmp$1 = side === "bottom" ? String(offset) + `px` : undefined;
  if (tmp$1 !== undefined) {
    tmp.bottom = tmp$1;
  }
  let tmp$2 = side === "top" ? String(offset) + `px` : undefined;
  if (tmp$2 !== undefined) {
    tmp.top = tmp$2;
  }
  return tmp;
}

function style$5(maxWidth, bottomBorder) {
  let maxWidth$1 = Stdlib_Option.mapOr(maxWidth, "auto", width => String(width) + "px");
  return {
    backgroundColor: "#fefefe",
    borderBottom: "1px solid",
    borderBottomColor: bottomBorder ? "#e7e7ee" : "transparent",
    display: "flex",
    height: "100%",
    maxWidth: maxWidth$1,
    overflow: "hidden",
    position: "relative",
    width: "100%",
    flexDirection: "column"
  };
}

let loadingBarAnchorStyle = {
  height: "0",
  position: "relative",
  zIndex: "3",
  flexShrink: "0"
};

let defaultDisabledRowsKeys = [];

let defaultErroredRowsMap = [];

function Table(props) {
  let loadingBarEstimatedDuration = props.loadingBarEstimatedDuration;
  let loadingBarStatus = props.loadingBarStatus;
  let onSelectChange = props.onSelectChange;
  let footerComponent = props.footerComponent;
  let __compactRows = props.compactRows;
  let maxHeight = props.maxHeight;
  let minHeight = props.minHeight;
  let maxWidth = props.maxWidth;
  let __initialAllSelected = props.initialAllSelected;
  let __selectAllEnabled = props.selectAllEnabled;
  let __selectionEnabled = props.selectionEnabled;
  let __erroredRowsMap = props.erroredRowsMap;
  let __disabledRowsKeys = props.disabledRowsKeys;
  let keyExtractor = props.keyExtractor;
  let rows = props.rows;
  let columns = props.columns;
  let disabledRowsKeys = __disabledRowsKeys !== undefined ? __disabledRowsKeys : defaultDisabledRowsKeys;
  let erroredRowsMap = __erroredRowsMap !== undefined ? __erroredRowsMap : defaultErroredRowsMap;
  let selectionEnabled = __selectionEnabled !== undefined ? __selectionEnabled : false;
  let selectAllEnabled = __selectAllEnabled !== undefined ? __selectAllEnabled : true;
  let initialAllSelected = __initialAllSelected !== undefined ? __initialAllSelected : false;
  let compactRows = __compactRows !== undefined ? __compactRows : false;
  let ref = React.useRef(null);
  let headerRef = React.useRef(null);
  let internalBodyRef = React.useRef(null);
  let bodyRef = Stdlib_Option.getOr(props.bodyRef, internalBodyRef);
  let match = React.useState(() => {
    if (initialAllSelected && selectAllEnabled) {
      return "All";
    } else {
      return {
        TAG: "Selected",
        _0: new Set([])
      };
    }
  });
  let setSelectedKeys = match[1];
  let selectedKeys = match[0];
  React.useEffect(() => {
    Stdlib_Option.forEach(onSelectChange, fn => {
      let selectedKeys$1;
      selectedKeys$1 = typeof selectedKeys !== "object" ? "All" : ({
          TAG: "Selected",
          _0: Array.from(selectedKeys._0)
        });
      if (selectionEnabled) {
        return fn(selectedKeys$1);
      }
    });
  }, [
    selectedKeys,
    rows.length !== 0
  ]);
  let columns$1 = React.useMemo(() => columns.filter(column => {
    let hidden = Stdlib_Option.getOr(Stdlib_Option.flatMap(column.layout, display => display.hidden), false);
    return hidden === false;
  }), [columns]);
  let match$1 = use();
  let fitContentRegistry = match$1[1];
  let fitContentWidths = match$1[0];
  let tableChildren = React.useMemo(() => {
    let columnByKey = new Map();
    columns$1.forEach(column => {
      columnByKey.set(column.key, column);
    });
    let rowIndexByKey = new Map();
    rows.forEach((row, index) => {
      let key = keyExtractor(row);
      if (!rowIndexByKey.has(key)) {
        rowIndexByKey.set(key, index);
        return;
      }
    });
    let headerColumns = columns$1.map(column => {
      let match = column.layout;
      if (match === undefined) {
        return column;
      }
      let match$1 = match.minWidth;
      let exit = 0;
      let match$2 = match.width;
      if (match$2 !== "fitContent") {
        exit = 2;
      }
      if (exit === 2) {
        if (match$1 === undefined) {
          return column;
        }
        if (match$1 !== "fitContent") {
          return column;
        }
      }
      let newrecord = {...column};
      newrecord.key = column.key;
      return newrecord;
    });
    return [
      JsxRuntime.jsx(ReactStately$1.TableHeader, {
        children: column => {
          let breakpoint = Stdlib_Option.flatMap(column.layout, display => display.breakpoint);
          let headerActionComponent = Stdlib_Option.flatMap(column.layout, layout => layout.headerActionComponent);
          let fitContentWidth = Stdlib_Option.getOr(fitContentWidths.get(column.key), 0);
          let minWidth = Stdlib_Option.flatMap(column.layout, display => display.minWidth);
          let match = minWidth !== undefined ? (
              typeof minWidth === "object" ? [
                  minWidth,
                  false
                ] : [
                  {
                    NAME: "px",
                    VAL: fitContentWidth
                  },
                  true
                ]
            ) : [
              undefined,
              false
            ];
          let minWidth$1 = Stdlib_Option.map(match[0], fromStaticToReactStatelyTableColumnSize);
          let width = Stdlib_Option.flatMap(column.layout, display => display.width);
          let match$1 = width !== undefined ? (
              typeof width === "object" ? [
                  width,
                  false
                ] : [
                  {
                    NAME: "px",
                    VAL: fitContentWidth
                  },
                  true
                ]
            ) : [
              undefined,
              false
            ];
          let widthFitsContent = match$1[1];
          let width$1 = Stdlib_Option.map(match$1[0], toReactStatelyTableColumnSize);
          let fitContent = widthFitsContent || match[1] && Stdlib_Option.isNone(breakpoint);
          let extraColumnProps = {
            breakpoint: Stdlib_Option.getOr(breakpoint, "xsmall"),
            headerActionComponent: headerActionComponent,
            margin: Stdlib_Option.flatMap(column.layout, display => display.margin),
            alignX: Stdlib_Option.flatMap(column.layout, display => display.alignX),
            alignY: Stdlib_Option.flatMap(column.layout, display => display.alignY),
            sticky: Stdlib_Option.flatMap(column.layout, display => Stdlib_Option.map(display.sticky, sticky => {
              if (sticky) {
                return rows.length <= 50;
              } else {
                return false;
              }
            })),
            fitContent: fitContent
          };
          let match$2 = width$1 !== undefined && breakpoint !== undefined && !widthFitsContent ? [
              undefined,
              width$1
            ] : [
              width$1,
              undefined
            ];
          let minWidth$2;
          let exit = 0;
          if (minWidth$1 !== undefined || !widthFitsContent) {
            exit = 1;
          } else {
            minWidth$2 = Primitive_option.some(fromStaticToReactStatelyTableColumnSize({
              NAME: "px",
              VAL: fitContentWidth
            }));
          }
          if (exit === 1) {
            minWidth$2 = breakpoint !== undefined ? Primitive_option.some(fromStaticToReactStatelyTableColumnSize({
                NAME: "px",
                VAL: 0
              })) : minWidth$1;
          }
          let allowsSorting = Stdlib_Option.getOr(column.allowsSorting, false);
          return JsxRuntime.jsx(ReactStately$1.Column, {
            children: Stdlib_Option.getOr(column.name, ""),
            minWidth: minWidth$2,
            width: match$2[0],
            defaultWidth: match$2[1],
            allowsSorting: allowsSorting,
            extraColumnProps: extraColumnProps
          }, column.key);
        },
        columns: headerColumns
      }),
      JsxRuntime.jsx(ReactStately$1.TableBody, {
        children: row => {
          let rowKey = keyExtractor(row);
          let disabled = disabledRowsKeys.some(current => current === rowKey);
          let index = Stdlib_Option.getOr(rowIndexByKey.get(rowKey), -1);
          let errorMessage = Stdlib_Option.map(erroredRowsMap.find(error => error.key === rowKey), error => error.message);
          return JsxRuntime.jsx(ReactStately$1.Row, {
            children: columnKey => {
              let column = Stdlib_Option.getExn(columnByKey.get(columnKey), undefined);
              return JsxRuntime.jsx(ReactStately$1.Cell, {
                children: column.render({
                  data: row,
                  index: index,
                  disabled: disabled,
                  errorMessage: errorMessage
                })
              }, rowKey + column.key);
            }
          }, rowKey);
        },
        items: rows
      })
    ];
  }, [
    columns$1,
    disabledRowsKeys,
    keyExtractor,
    rows,
    erroredRowsMap,
    fitContentWidths
  ]);
  let tmp;
  tmp = typeof selectedKeys !== "object" ? "all" : selectedKeys._0;
  let props_selectionMode = selectionEnabled ? "multiple" : "none";
  let props_allowDuplicateSelectionEvents = false;
  let props_sortDescriptor = props.sortDescriptor;
  let props_onSortChange = props.onSortChange;
  let props_onSelectionChange = keys => {
    let match = typeof keys;
    let value = match === "string" ? "All" : ({
        TAG: "Selected",
        _0: keys
      });
    setSelectedKeys(prev => {
      if (typeof value !== "object" && !selectAllEnabled) {
        return prev;
      } else {
        return value;
      }
    });
  };
  let props$1 = {
    children: tableChildren,
    selectionMode: props_selectionMode,
    showSelectionCheckboxes: selectionEnabled,
    allowDuplicateSelectionEvents: props_allowDuplicateSelectionEvents,
    disabledBehavior: "all",
    disabledKeys: disabledRowsKeys,
    selectedKeys: tmp,
    sortDescriptor: props_sortDescriptor,
    onSortChange: props_onSortChange,
    onSelectionChange: props_onSelectionChange
  };
  let state = ReactStately$1.useTableState(props$1);
  let layout = use$1(ref, headerRef, bodyRef, state, props.onLoadMore);
  let props$2 = {
    "aria-label": "table",
    scrollRef: bodyRef
  };
  let match$2 = ReactAria$1.useTable(props$2, state, ref);
  let gridProps = match$2.gridProps;
  let stickyRightWidth = getStickyRightWidth(layout.state);
  let stickyLeftWidth = getStickyLeftWidth(layout.state);
  let stickyScrollPosition = columns$1.some(c => Stdlib_Option.getOr(Stdlib_Option.flatMap(c.layout, l => l.sticky), false)) && rows.length <= 50 ? layout.scrollBarX.position : 0;
  let match$3 = Stdlib_Option.mapOr(Primitive_option.fromNullable(state.sortDescriptor), [
    "",
    ""
  ], descriptor => [
    descriptor.column,
    descriptor.direction
  ]);
  return React.useMemo(() => {
    let newrecord = {...gridProps};
    return JsxRuntime.jsxs("table", (newrecord.style = style$5(maxWidth, layout.scrollBarY.scrollable), newrecord["aria-busy"] = Stdlib_Option.map(loadingBarStatus, status => status === "pending"), newrecord.ref = Primitive_option.some(ref), newrecord.children = [
      JsxRuntime.jsx(make$3, {
        children: state.collection.headerRows.map(headerRow => JsxRuntime.jsx(make, {
          children: ReactStately.Table.collectionToArray(headerRow.childNodes).map(column => {
            if (column.props.isSelectionCell) {
              return JsxRuntime.jsx(make$1, {
                column: column,
                state: state,
                layout: layout,
                disabled: !selectAllEnabled
              }, column.key);
            } else {
              return JsxRuntime.jsx(make$2, {
                column: column,
                state: state,
                layout: layout,
                fitContentRegistry: fitContentRegistry
              }, column.key);
            }
          }),
          item: headerRow,
          state: state,
          layout: layout
        }, headerRow.key)),
        type_: "thead",
        layout: layout
      }),
      loadingBarStatus !== undefined ? JsxRuntime.jsx("div", {
          children: JsxRuntime.jsx(PredictiveLoadingBar.make, {
            status: loadingBarStatus,
            estimatedDuration: loadingBarEstimatedDuration
          }),
          style: loadingBarAnchorStyle
        }) : null,
      JsxRuntime.jsxs(make$3, {
        children: [
          ReactStately.Table.collectionToArray(state.collection.body.childNodes).map((row, rowIdx) => {
            let errored = erroredRowsMap.some(error => error.key === row.key);
            return JsxRuntime.jsx(make$4, {
              children: ReactStately.Table.collectionToArray(row.childNodes).map(cell => {
                let fromLastRow = rowIdx === (state.collection.size - 1 | 0);
                if (cell.props.isSelectionCell) {
                  return JsxRuntime.jsx(make$5, {
                    fromLastRow: fromLastRow,
                    cell: cell,
                    state: state,
                    layout: layout
                  }, cell.key);
                } else {
                  return JsxRuntime.jsx(make$6, {
                    fromLastRow: fromLastRow,
                    compactRows: compactRows,
                    cell: cell,
                    state: state,
                    layout: layout,
                    fitContentRegistry: fitContentRegistry
                  }, cell.key);
                }
              }),
              item: row,
              state: state,
              layout: layout,
              errored: errored,
              compactRows: compactRows
            }, row.key);
          }),
          Stdlib_Option.getOr(footerComponent, null)
        ],
        type_: "tbody",
        layout: layout,
        minHeight: minHeight,
        maxHeight: maxHeight
      }),
      JsxRuntime.jsx(AnimatedRender.make, {
        children: JsxRuntime.jsx("div", {
          style: verticalGradientOverflowStyle("right", Stdlib_Option.getOr(layout.scrollBarY.size, 0) + stickyRightWidth, layout.bodyHeight, undefined)
        }),
        displayed: layout.scrollBarX.scrollable,
        duration: 0
      }),
      JsxRuntime.jsx(AnimatedRender.make, {
        children: JsxRuntime.jsx("div", {
          style: verticalGradientOverflowStyle("left", stickyLeftWidth, layout.bodyHeight, undefined)
        }),
        displayed: layout.scrollBarX.position > 0,
        duration: 0
      }),
      JsxRuntime.jsx(AnimatedRender.make, {
        children: JsxRuntime.jsx("div", {
          style: horizontalGradientOverflowStyle("bottom", Stdlib_Option.getOr(layout.scrollBarX.size, 0), undefined)
        }),
        displayed: layout.scrollBarY.scrollable,
        animation: "fade",
        duration: 450
      }),
      JsxRuntime.jsx(AnimatedRender.make, {
        children: JsxRuntime.jsx("div", {
          style: horizontalGradientOverflowStyle("top", 25, undefined)
        }),
        displayed: layout.scrollBarY.position > 0,
        animation: "fade",
        duration: 450
      })
    ], newrecord));
  }, [
    selectAllEnabled && selectionEnabled,
    selectedKeys,
    tableChildren,
    stickyScrollPosition,
    layout.ariaState.updateResizedColumns,
    layout.tableWidth,
    layout.bodyHeight,
    layout.scrollBarX.scrollable,
    layout.scrollBarX.position > 0,
    layout.scrollBarX.size,
    layout.scrollBarY.scrollable,
    layout.scrollBarY.position > 0,
    layout.scrollBarY.size,
    stickyRightWidth,
    stickyLeftWidth,
    state.selectionManager.focusedKey,
    match$3[0],
    match$3[1],
    loadingBarStatus,
    loadingBarEstimatedDuration
  ]);
}

let TableLayout = {
  computeColumnStickyPositionState: computeColumnStickyPositionState,
  getRelativeBoundingRect: getRelativeBoundingRect
};

let ColumnSize = {};

let make$7 = Table;

export {
  TableLayout,
  ColumnSize,
  make$7 as make,
}
/* selectCellWidth Not a pure module */
